import { ChangeEvent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { ShareReferenceLocationState } from '$types/LocationState';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { useCaptchaValidation } from '$hooks/useCaptchaValidation';
import { NameRegex, emailRegex } from '../../constants/regex';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useRegisterForm from '../../hooks/useRegisterForm';
import { ReactComponent as GoogleIcon } from '../../icons/GoogleColorIcon.svg';
import { ReactComponent as LinkedInIcon } from '../../icons/LinkedInBlueIcon.svg';
import {
  employerEmailAuthAction,
  employerGoogleAuthAction,
  employerLinkedInAuthAction,
} from '../../store/legacy/auth';
import ResendEmailModal from './ResendEmailModal/ResendEmailModal';

interface FormTypes {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  companyName?: string;
  companyURL?: string;
  phone: string;
  residenceCountry?: string;
  platformReferral?: string;
}

interface EmployerRegisterFormProps {
  successRedirectUrl?: string;
}

const EmployerRegisterForm = ({
  successRedirectUrl,
}: EmployerRegisterFormProps = {}) => {
  const location = useLocation() as ShareReferenceLocationState;
  const [searchParams] = useSearchParams();
  const shareCandidatesIds =
    location?.state?.shareCandidatesIds ||
    searchParams.get('shareCandidatesIds') ||
    undefined;
  const emailFromQuery = searchParams.get('email') || undefined;
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    watch,
  } = useForm<FormTypes>({
    mode: 'onChange',
    defaultValues: { email: emailFromQuery },
  });
  const [t, i18next] = useTranslation('global');
  const { displayName } = useAppSelector((state) => state.brand);
  const dispatch = useAppDispatch();
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const passwordWatch = watch('password', '');
  const { isMobile, authState, passStrength, passwordValidator } =
    useRegisterForm();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState(false);
  const {
    validateCaptcha,
    validateV2Captcha,
    showV2Captcha,
    v2CaptchaCompleted,
    isValidating,
    recaptchaV2Ref,
    v2SiteKey,
    onV2CaptchaChange,
  } = useCaptchaValidation();
  const CAPTCHA_ACTION = 'register_form';

  const passColors = [
    'white',
    '#f44336',
    'purple',
    'blueviolet',
    'cyan',
    '#05668D',
  ];

  useEffect(() => {
    if (authState.isLoggedIn) {
      navigate(
        shareCandidatesIds
          ? `/share/candidates/${shareCandidatesIds}`
          : '/employer-dashboard'
      );
    }
  }, [authState.isLoggedIn, navigate, shareCandidatesIds]);

  useEffect(() => {
    if (!authState.loading && authState.employer?.employerRegisterSuccess) {
      if (successRedirectUrl) {
        window.location.href = successRedirectUrl;
      } else {
        setShowModal(true);
      }
    }
  }, [
    authState.loading,
    authState.employer?.employerRegisterSuccess,
    successRedirectUrl,
  ]);

  // Form declaration
  const firstName = register('firstName', {
    required: t('register.nameInputValid').toString(),
    maxLength: {
      value: 100,
      message: t('register.invalidFirstNameInput').toString(),
    },
    pattern: {
      value: NameRegex,
      message: t('register.invalidFirstNameInput').toString(),
    },
  });
  const lastName = register('lastName', {
    required: t('register.lastNameInputValid').toString(),
    maxLength: {
      value: 100,
      message: t('register.invalidLastNameInput').toString(),
    },
    pattern: {
      value: NameRegex,
      message: t('register.invalidLastNameInput').toString(),
    },
  });
  const email = register('email', {
    required: t('register.emailInputValid').toString(),
    maxLength: {
      value: 100,
      message: t('register.invalidEmailInput').toString(),
    },
    pattern: {
      value: emailRegex,
      message: 'Invalid email',
    },
  });
  const password = register('password', {
    validate: {
      validPass: (val) =>
        passwordValidator(val) >= 1 || 'Must contain at least 6 characters',
    },
  });
  const confirmPassword = register('confirmPassword', {
    required: t('register.confirmPasswordValid').toString(),
    validate: {
      passwordEqual: (value) =>
        value === getValues().password || t('register.matchPass').toString(),
    },
  });

  const companyName = register('companyName', {
    required: t('register.companyRequired').toString(),
  });
  const companyURL = register('companyURL');

  const submitForm = async (values: FormTypes) => {
    const { companyName: companyNameField } = values;
    const { companyURL: companyURLField } = values;

    let isCaptchaValid = false;

    if (showV2Captcha) {
      // If v2 captcha is showing, validate v2 token
      isCaptchaValid = await validateV2Captcha();
    } else {
      // Try v3 validation first
      isCaptchaValid = await validateCaptcha(CAPTCHA_ACTION);
    }

    if (companyNameField && isCaptchaValid) {
      dispatch(
        employerEmailAuthAction(
          {
            firstName: values.firstName.trim().replace(/\s\s+/g, ' '),
            lastName: values.lastName.trim().replace(/\s\s+/g, ' '),
            email: values.email.toLowerCase().trim(),
            password: values.password,
            shareReferenceId: location?.state?.shareReferenceId,
            shareCandidatesRosterId: shareCandidatesIds,
          },
          {
            CompanyName: companyNameField.trim(),
            CompanyURL: companyURLField?.trim(),
          },
          i18next.language
        )
      );
    }
  };

  const onGoogleSignIn = () => {
    dispatch(
      employerGoogleAuthAction(
        {
          CompanyName: 'Google Sign in (No company name)',
          ShareReferenceId: location?.state?.shareReferenceId,
          shareCandidatesRosterId: shareCandidatesIds,
        },
        i18next.language
      )
    );
  };

  const onLinkedInSignIn = () => {
    dispatch(
      employerLinkedInAuthAction(
        {
          CompanyName: 'LinkedIn Sign in (No company name)',
          ShareReferenceId: location?.state?.shareReferenceId,
          shareCandidatesRosterId: shareCandidatesIds,
        },
        i18next.language
      )
    );
  };

  const closeModal = (): void => {
    setShowModal(false);
  };

  return (
    <>
      <ResendEmailModal
        showModal={showModal}
        closeModal={closeModal}
        isMobile={!!isMobile}
        authState={authState}
      />
      <form
        id="register-form"
        data-testid="register-form"
        className="flex flex-col gap-12"
        onSubmit={handleSubmit(submitForm)}
        style={{
          WebkitFilter:
            !isMobile && showModal ? 'blur(1.5px)' : '' /* Safari 6.0 - 9.0 */,
          filter: !isMobile && showModal ? 'blur(1.5px)' : '',
          zIndex: showModal && isMobile ? '-10' : '1',
        }}
      >
        <div className="flex flex-col gap-4">
          <div className="flex gap-4">
            <div className="w-full flex flex-col gap-1">
              <label
                htmlFor="firstName"
                className="text-spk-mediumContrast text-xs"
              >
                {t('register.nameInput')}
              </label>
              <IconField iconPosition="left" className="w-full">
                <InputIcon className="pi pi-user"> </InputIcon>
                <InputText
                  onChange={(e: ChangeEvent) => firstName.onChange(e)}
                  name={firstName.name}
                  onBlur={firstName.onBlur}
                  ref={firstName.ref}
                  type="text"
                  className={`w-full ${
                    errors.firstName?.message && 'border-spk-brightRed'
                  }`}
                />
              </IconField>
              {errors.firstName?.message && (
                <small data-testid="input-error" className="text-spk-brightRed">
                  {errors.firstName?.message}
                </small>
              )}
            </div>
            <div className="w-full flex flex-col gap-1">
              <label
                htmlFor="lastName"
                className="text-spk-mediumContrast text-xs"
              >
                {t('register.lastNameInput')}
              </label>
              <IconField iconPosition="left" className="w-full">
                <InputIcon className="pi pi-user"> </InputIcon>
                <InputText
                  onChange={(e: ChangeEvent) => lastName.onChange(e)}
                  name={lastName.name}
                  onBlur={lastName.onBlur}
                  ref={lastName.ref}
                  type="text"
                  className={`w-full ${
                    errors.lastName?.message && 'border-spk-brightRed'
                  }`}
                />
              </IconField>
              {errors.lastName?.message && (
                <small data-testid="input-error" className="text-spk-brightRed">
                  {errors.lastName?.message}
                </small>
              )}
            </div>
          </div>

          <div className="flex gap-4">
            <div className="w-full flex flex-col gap-1">
              <label
                htmlFor="email"
                className="text-spk-mediumContrast text-xs"
              >
                {t('register.emailInput')}
              </label>
              <IconField iconPosition="left" className="w-full">
                <InputIcon className="pi pi-envelope"> </InputIcon>
                <InputText
                  onChange={(e: ChangeEvent) => email.onChange(e)}
                  name={email.name}
                  onBlur={email.onBlur}
                  ref={email.ref}
                  type="text"
                  className={`w-full ${
                    errors.email?.message && 'border-spk-brightRed'
                  }`}
                />
              </IconField>
              {errors.email?.message && (
                <small data-testid="input-error" className="text-spk-brightRed">
                  {errors.email?.message}
                </small>
              )}
            </div>
            <div className="w-full flex flex-col gap-1">
              <label
                htmlFor="companyName"
                className="text-spk-mediumContrast text-xs"
              >
                {t('register.companyName')}
              </label>
              <IconField iconPosition="left" className="w-full">
                <InputIcon className="pi pi-building"> </InputIcon>
                <InputText
                  onChange={(e: ChangeEvent) => companyName.onChange(e)}
                  name={companyName.name}
                  onBlur={companyName.onBlur}
                  ref={companyName.ref}
                  type="text"
                  className={`w-full ${
                    errors.companyName?.message && 'border-spk-brightRed'
                  }`}
                />
              </IconField>
              {errors.companyName?.message && (
                <small data-testid="input-error" className="text-spk-brightRed">
                  {errors.companyName?.message}
                </small>
              )}
            </div>
          </div>
          <div className="w-full flex flex-col gap-1">
            <label
              htmlFor="companyURL"
              className="text-spk-mediumContrast text-xs"
            >
              {t('register.companyURL')}
            </label>
            <IconField iconPosition="left" className="w-full">
              <InputIcon className="pi pi-building"> </InputIcon>
              <InputText
                onChange={(e: ChangeEvent) => companyURL.onChange(e)}
                name={companyURL.name}
                onBlur={companyURL.onBlur}
                ref={companyURL.ref}
                type="text"
                className={`w-full ${
                  errors.companyURL?.message && 'border-spk-brightRed'
                }`}
              />
            </IconField>
          </div>

          <div className="w-full flex flex-col gap-1">
            <label
              htmlFor="password"
              className="text-spk-mediumContrast text-xs"
            >
              {t('register.passwordInput')}
            </label>
            <IconField
              iconPosition="left"
              className="w-full p-icon-field-right"
            >
              <InputIcon className="pi pi-key"> </InputIcon>
              <InputText
                onChange={(e: ChangeEvent) => password.onChange(e)}
                name={password.name}
                onBlur={password.onBlur}
                ref={password.ref}
                type={isPasswordVisible ? 'text' : 'password'}
                className={`w-full ${
                  errors.password?.message && 'border-spk-brightRed'
                }`}
              />
              <InputIcon
                onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                className={`cursor-pointer pi ${
                  isPasswordVisible ? 'pi-eye-slash' : 'pi-eye'
                }`}
              />
            </IconField>
            {errors.password?.message && (
              <small data-testid="input-error" className="text-spk-brightRed">
                {errors.password?.message}
              </small>
            )}
          </div>
          <div className="w-full flex flex-col">
            <div className="w-full h-2.5 bg-employer-secondary rounded-full">
              <div
                style={{
                  width: `${(passStrength / 4) * 100}%`,
                  background: passColors[passStrength],
                }}
                className="h-2.5 rounded-full"
              />
            </div>
            {passwordWatch.length >= 1 && (
              <p className="w-full text-spk-mediumContrast text-sm">
                {t('register.passStrength')}
                {t(`register.strength${passStrength}`)}
              </p>
            )}
          </div>

          <div className="w-full flex flex-col gap-1">
            <label
              htmlFor="confirmPassword"
              className="text-spk-mediumContrast text-xs"
            >
              {t('register.confirmPassword')}
            </label>
            <IconField
              iconPosition="left"
              className="w-full p-icon-field-right"
            >
              <InputIcon className="pi pi-key"> </InputIcon>
              <InputText
                onChange={(e: ChangeEvent) => confirmPassword.onChange(e)}
                name={confirmPassword.name}
                onBlur={confirmPassword.onBlur}
                ref={confirmPassword.ref}
                type={isConfirmPasswordVisible ? 'text' : 'password'}
                className={`w-full ${
                  errors.confirmPassword?.message && 'border-spk-brightRed'
                }`}
              />
              <InputIcon
                onClick={() =>
                  setIsConfirmPasswordVisible(!isConfirmPasswordVisible)
                }
                className={`cursor-pointer pi ${
                  isConfirmPasswordVisible ? 'pi-eye-slash' : 'pi-eye'
                }`}
              />
            </IconField>
            {errors.confirmPassword?.message && (
              <small data-testid="input-error" className="text-spk-brightRed">
                {errors.confirmPassword?.message}
              </small>
            )}
          </div>
          <div className="flex justify-start font-sm">
            <p className="text-spk-mediumContrast">
              {t('register.byCreating', { brandName: displayName })}&nbsp;
            </p>
            <a
              href="https://info.simera.io/terms-of-service"
              target="_blank"
              rel="noreferrer"
              className="font-bold text-employer-primary underline"
            >
              {t('register.TOS', { brandName: displayName })}
            </a>
          </div>
        </div>
        {showV2Captcha && (
          <div className="flex justify-center">
            <ReCAPTCHA
              ref={recaptchaV2Ref}
              sitekey={v2SiteKey}
              onChange={onV2CaptchaChange}
            />
          </div>
        )}
        <div>
          <Button
            severity="success"
            type="submit"
            onClick={handleSubmit(submitForm)}
            label={t('register.createAccBtn')}
            className="w-full"
            disabled={showV2Captcha && !v2CaptchaCompleted}
            loading={isValidating}
          />
          <Divider align="center" className="py-8 m-0 ">
            <p className="text-xs font-normal">{t('common.or')}</p>
          </Divider>
          <div
            data-testid="login-buttons"
            className="w-full flex gap-4 justify-center"
          >
            <Button
              severity="secondary"
              className="w-fill-available flex gap-4"
              outlined
              style={{ border: '1px solid' }}
              onClick={onGoogleSignIn}
            >
              <GoogleIcon />
              <p>Google</p>
            </Button>
            <Button
              severity="secondary"
              className="w-fill-available flex gap-4"
              outlined
              style={{ border: '1px solid' }}
              onClick={onLinkedInSignIn}
            >
              <LinkedInIcon />
              <p>LinkedIn</p>
            </Button>
          </div>
        </div>
      </form>
    </>
  );
};

EmployerRegisterForm.defaultProps = {
  successRedirectUrl: undefined,
};

export default EmployerRegisterForm;
